import { z } from "zod";
import { zLanguage } from "./i18n.js";

/** Semantic tokens every theme must define. Values are CSS colors. */
export const THEME_TOKEN_KEYS = [
  "surface-1",
  "surface-2",
  "surface-3",
  "border",
  "border-strong",
  "text-primary",
  "text-secondary",
  "text-tertiary",
  "accent",
  "accent-contrast",
  "success",
  "warning",
  "danger",
  "info",
] as const;
export type ThemeTokenKey = (typeof THEME_TOKEN_KEYS)[number];

export const zThemeTokens = z.record(
  z.enum(THEME_TOKEN_KEYS),
  z.string().regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, "hex color required"),
);
export type ThemeTokens = z.infer<typeof zThemeTokens>;

export const zOrgTheme = z.object({
  id: z.string().uuid(),
  name: z.string(),
  mode: z.enum(["light", "dark"]),
  tokens: zThemeTokens,
  isPreset: z.boolean(),
});
export type OrgTheme = z.infer<typeof zOrgTheme>;

export const zUserPreferences = z.object({
  colorScheme: z.enum(["light", "dark", "system"]).default("system"),
  highContrast: z.boolean().default(false),
  accent: z.enum(["indigo", "blue", "teal", "green", "amber", "rose", "violet"]).default("green"),
  density: z.enum(["comfortable", "compact"]).default("comfortable"),
  fontSizeStep: z.number().int().min(-1).max(2).default(0),
  reducedMotion: z.boolean().default(false),
  /** saved custom reports pinned to this user's dashboard, in display order */
  dashboardReportIds: z.array(z.string().uuid()).max(12).default([]),
  /** per-card body height (px) on the dashboard, keyed by report id */
  dashboardCardHeights: z.record(z.number().int().min(180).max(800)).default({}),
  /** the first-login product tour has been shown (or dismissed) */
  onboardingTourDone: z.boolean().default(false),
  /** CT-RM-30: app UI language (unset = the org default language) */
  language: zLanguage.optional(),
});
export type UserPreferences = z.infer<typeof zUserPreferences>;

/** Relative luminance per WCAG 2.x */
function luminance(hex: string): number {
  const c = hex.replace("#", "").slice(0, 6);
  const [r, g, b] = [0, 2, 4].map((i) => {
    const v = parseInt(c.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Validates WCAG AA contrast for a custom theme. Returns a list of failures (empty = pass). */
export function validateThemeContrast(tokens: ThemeTokens): string[] {
  const failures: string[] = [];
  const pairs: [ThemeTokenKey, ThemeTokenKey, number, string][] = [
    ["text-primary", "surface-1", 4.5, "Body text on main surface"],
    ["text-primary", "surface-2", 4.5, "Body text on raised surface"],
    ["text-secondary", "surface-1", 4.5, "Secondary text on main surface"],
    ["accent-contrast", "accent", 4.5, "Text on accent (buttons)"],
  ];
  for (const [fg, bg, min, label] of pairs) {
    const f = tokens[fg];
    const g = tokens[bg];
    if (!f || !g) continue;
    const ratio = contrastRatio(f, g);
    if (ratio < min) failures.push(`${label}: contrast ${ratio.toFixed(2)}:1 is below WCAG AA ${min}:1`);
  }
  return failures;
}
